import styled, { css, keyframes } from "styled-components";

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ gap = 3, theme }) => theme.space(gap)};
`;

export const Row = styled.div`
  display: flex;
  align-items: ${({ align = "center" }) => align};
  justify-content: ${({ justify = "flex-start" }) => justify};
  gap: ${({ gap = 3, theme }) => theme.space(gap)};
  flex-wrap: ${({ wrap = "nowrap" }) => wrap};
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${({ theme }) => theme.space(4)};
  margin-bottom: ${({ theme }) => theme.space(6)};

  ${({ theme }) => theme.media.md} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PageTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: 400;
  font-size: clamp(28px, 4vw, 36px);
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

export const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 14px;
  margin-top: ${({ theme }) => theme.space(1)};
`;

export const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme, padded = 5 }) => theme.space(padded)};
  box-shadow: ${({ theme }) => theme.shadow.xs};
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space(2)};
  padding: 10px 16px;
  font-weight: 500;
  font-size: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.06s ease, box-shadow 0.15s ease, color 0.15s ease;
  white-space: nowrap;
  user-select: none;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const Button = styled.button`
  ${buttonBase};
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  border-color: ${({ theme }) => theme.colors.accent};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.accentHover};
    border-color: ${({ theme }) => theme.colors.accentHover};
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border-color: ${({ theme }) => theme.colors.borderStrong};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

export const GhostButton = styled.button`
  ${buttonBase};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 8px 12px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgAlt};
    color: ${({ theme }) => theme.colors.text};
  }
`;

export const DangerButton = styled.button`
  ${buttonBase};
  background: ${({ theme }) => theme.colors.danger};
  color: #fff;
  border-color: ${({ theme }) => theme.colors.danger};

  &:hover:not(:disabled) { filter: brightness(0.94); }
`;

export const IconButton = styled.button`
  ${buttonBase};
  width: 36px;
  height: 36px;
  padding: 0;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textMuted};
  border-color: ${({ theme }) => theme.colors.border};

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.accent};
    border-color: ${({ theme }) => theme.colors.accent};
    background: ${({ theme }) => theme.colors.surface};
  }

  svg { width: 16px; height: 16px; }
`;

export const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.space(1.5)};
`;

const fieldBase = css`
  width: 100%;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme, error }) => (error ? theme.colors.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder { color: ${({ theme }) => theme.colors.textSubtle}; }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.bgAlt};
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

export const Input = styled.input`
  ${fieldBase};
`;

export const Select = styled.select`
  ${fieldBase};
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='%236B6864' d='M2.5 4.5l3.5 3.5 3.5-3.5z'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 32px;
`;

export const Textarea = styled.textarea`
  ${fieldBase};
  resize: vertical;
  min-height: 80px;
`;

export const FieldError = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.space(1)};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 12px;
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space(1.5)};
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  ${({ tone = "neutral", theme }) => {
    const map = {
      neutral: { bg: theme.colors.bgAlt, color: theme.colors.textMuted, border: theme.colors.border },
      success: { bg: theme.colors.successSoft, color: theme.colors.success, border: "#A8E0B9" },
      danger: { bg: theme.colors.dangerSoft, color: theme.colors.danger, border: "#FBC4C4" },
      warning: { bg: theme.colors.warningSoft, color: theme.colors.warning, border: "#FBD888" },
      info: { bg: theme.colors.infoSoft, color: theme.colors.info, border: "#B6CDFB" },
      accent: { bg: theme.colors.accentSoft, color: theme.colors.accent, border: "#EFC7B5" },
    };
    const t = map[tone] || map.neutral;
    return css`
      background: ${t.bg};
      color: ${t.color};
      border: 1px solid ${t.border};
    `;
  }}
`;

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ color, theme }) => color || theme.colors.success};
`;

export const Mono = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.bgAlt};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 12px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space(2)};
  padding: ${({ theme }) => theme.space(10)};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;

  h3 {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-weight: 400;
    font-size: 22px;
    color: ${({ theme }) => theme.colors.text};
  }
  p { font-size: 14px; max-width: 360px; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  width: ${({ size = 20 }) => `${size}px`};
  height: ${({ size = 20 }) => `${size}px`};
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.accent};
  animation: ${spin} 0.7s linear infinite;
`;

export const SpinnerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(31, 30, 29, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(2px);
`;

export const DayChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 32px;
  padding: 0 8px;
  margin: 2px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  border: 1px solid
    ${({ selected, theme }) => (selected ? theme.colors.accent : theme.colors.border)};
  background: ${({ selected, theme }) => (selected ? theme.colors.accent : theme.colors.surface)};
  color: ${({ selected, theme }) => (selected ? "#fff" : theme.colors.text)};

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.space(5)} 0;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.space(4)};
`;

export const Stat = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.space(5)};

  .label {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.space(2)};
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  .value {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-weight: 400;
    font-size: 36px;
    line-height: 1.1;
    margin-top: ${({ theme }) => theme.space(2)};
    letter-spacing: -0.02em;
  }

  .sub {
    margin-top: ${({ theme }) => theme.space(1)};
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`;

export const Skeleton = styled.div`
  height: ${({ h = 16 }) => `${h}px`};
  width: ${({ w = "100%" }) => (typeof w === "number" ? `${w}px` : w)};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgAlt} 0%,
    ${({ theme }) => theme.colors.surfaceHover} 50%,
    ${({ theme }) => theme.colors.bgAlt} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;
